import type { ColumnTable } from 'arquero';
import * as aq from 'arquero';

import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';

/**
 * Single source of truth for the {@link Dataset} → Arquero {@link ColumnTable}
 * boundary in the engine.
 *
 * Builds an Arquero table whose column set matches `dataset.columns`
 * exactly: every declared column is materialised on the table — even
 * when no row contains it — and only declared columns appear.
 * Missing keys (`row[name] === undefined`) are normalised to `null`
 * because Arquero rejects `undefined` cell values.
 *
 * Why not `aq.from(dataset.rows)`? Arquero infers column names from
 * `Object.keys(rows[0])` only. When
 * rows are sparse (e.g. an entity dataset where `endDateTime` is set
 * solely on terminated entities, and the first row happens to be
 * active), the resulting table's column set diverges from the
 * dataset's schema. Downstream operators that consult the schema —
 * notably `joinDatasets`'s conflict-rename pass — then try to
 * `select` / `rename` a column the table doesn't know and crash with
 * `Unrecognized column: <name>`.
 *
 * Routing every Dataset → ColumnTable conversion through this helper
 * keeps the schema authoritative and makes the row-shape contract
 * uniform across the engine.
 */
export function datasetToTable(dataset: Dataset): ColumnTable {
  const cols: Record<string, unknown[]> = {};
  for (const col of dataset.columns) {
    cols[col.name] = dataset.rows.map((row) => {
      const value = (row as Record<string, unknown>)[col.name];
      return value === undefined ? null : value;
    });
  }
  return aq.table(cols);
}
